import type { ChatSummary, DealDetails } from '@erp/shared';
import { useQuery } from '@tanstack/react-query';
import { Plus, Wrench } from 'lucide-react';
import { useState } from 'react';
import { useCan } from '@/lib/auth';
import { chatMessagesQuery } from '@/lib/crm';
import { equipmentListQuery, ticketsQuery } from '@/lib/equipment';
import { type ClientValue, EquipmentFormDialog, TicketFormDialog } from './equipment-forms';
import { EquipmentTable, TicketTable } from './equipment-tables';
import { Button } from './ui';

function useEquipmentAccess() {
  const can = useCan();
  return {
    sees: can('equipment.read') || can('helpdesk.access') || can('production.manage'),
    createsTicket: can('helpdesk.ticket.create') || can('helpdesk.access'),
    produces: can('production.manage'),
  };
}

/** Кнопка «Сервисная заявка» в карточке клиента. */
export function NewTicketButton({
  client,
  label = 'Сервисная заявка',
}: {
  client: ClientValue;
  label?: string;
}) {
  const access = useEquipmentAccess();
  const [open, setOpen] = useState(false);
  if (!access.createsTicket) return null;
  return (
    <>
      <Button variant="secondary" onClick={() => setOpen(true)}>
        <Wrench className="size-4" aria-hidden />
        {label}
      </Button>
      {open && <TicketFormDialog defaults={{ client }} onClose={() => setOpen(false)} />}
    </>
  );
}

/** Оборудование клиента и его сервисные заявки — в карточке контакта или компании. */
export function ClientEquipment({ contactId, companyId }: { contactId?: string; companyId?: string }) {
  const access = useEquipmentAccess();
  const equipment = useQuery({
    ...equipmentListQuery({ contactId, companyId, lifecycle: 'all' }),
    enabled: access.sees,
  });
  const tickets = useQuery({ ...ticketsQuery({ contactId, companyId, state: 'all' }), enabled: access.sees });
  if (!access.sees) return null;
  const units = equipment.data?.items ?? [];
  const list = tickets.data?.items ?? [];
  if (!units.length && !list.length) return null;
  return (
    <>
      {units.length > 0 && (
        <section>
          <h2 className="mb-2 font-semibold">Оборудование</h2>
          <EquipmentTable items={units} compact />
        </section>
      )}
      {list.length > 0 && (
        <section>
          <h2 className="mb-2 font-semibold">Сервисные заявки</h2>
          <TicketTable items={list.slice(0, 10)} compact />
        </section>
      )}
    </>
  );
}

/** Стенды по сделке: запуск в производство прямо из сделки. */
export function DealEquipment({ deal }: { deal: DealDetails }) {
  const access = useEquipmentAccess();
  const [launching, setLaunching] = useState(false);
  const list = useQuery({
    ...equipmentListQuery({ dealId: deal.id, lifecycle: 'all' }),
    enabled: access.sees,
  });
  if (!access.sees) return null;
  const items = list.data?.items ?? [];
  if (!items.length && !access.produces) return null;
  const client: ClientValue | null = deal.contact
    ? {
        kind: 'contact',
        id: deal.contact.id,
        label: deal.company ? `${deal.contact.fullName} · ${deal.company.name}` : deal.contact.fullName,
        companyId: deal.company?.id ?? null,
      }
    : deal.company
      ? { kind: 'company', id: deal.company.id, label: deal.company.name, companyId: deal.company.id }
      : null;
  return (
    <section>
      <div className="mb-2 flex items-center justify-between gap-2">
        <h2 className="font-semibold">Стенды по сделке</h2>
        {access.produces && (
          <Button variant="ghost" size="sm" onClick={() => setLaunching(true)}>
            <Plus className="size-3.5" aria-hidden />В производство
          </Button>
        )}
      </div>
      {launching && (
        <EquipmentFormDialog
          defaults={{ client, dealId: deal.id, dealLabel: `#${deal.number} ${deal.title}` }}
          onClose={() => setLaunching(false)}
        />
      )}
      {items.length ? (
        <EquipmentTable items={items} compact />
      ) : (
        <p className="panel px-4 py-3 text-[13.5px] text-steel">
          Стенд по сделке ещё не запущен в производство.
        </p>
      )}
    </section>
  );
}

/** «Заявка» из чата: клиент — собеседник, текст — последнее сообщение клиента. */
export function ChatTicketButton({ chat }: { chat: ChatSummary }) {
  const access = useEquipmentAccess();
  const messages = useQuery({ ...chatMessagesQuery(chat.id), enabled: access.createsTicket });
  const [open, setOpen] = useState(false);
  if (!access.createsTicket) return null;
  const lastIncoming = [...(messages.data ?? [])].reverse().find((m) => m.direction === 'in' && m.text);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="ml-auto inline-flex h-7 shrink-0 items-center gap-1.5 rounded px-2 text-[12.5px] text-ink-soft hover:bg-sunken hover:text-ink"
        title="Создать сервисную заявку по этому чату"
      >
        <Wrench className="size-3.5" aria-hidden />
        Заявка
      </button>
      {open && (
        <TicketFormDialog
          defaults={{
            chatId: chat.id,
            client: chat.contact
              ? { kind: 'contact', id: chat.contact.id, label: chat.contact.fullName, companyId: null }
              : null,
            subject: lastIncoming?.text?.split('\n')[0]?.slice(0, 120),
            description: lastIncoming?.text ?? undefined,
          }}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}
